import { useState } from 'react'
import { PlusIcon, TrashIcon, InfoIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopTextField,
    ScoopSelector,
    ScoopText,
    ScoopTooltip,
    ScoopTable,
    ScoopRow,
} from '../../../../../../ui'
import styles from './DefinitionTable.module.scss'

const FormulaSyntaxHelp = () => (
    <div className={styles.formulaHelp}>
        <ScoopText className={styles.formulaHelpTitle}>Formula Syntax</ScoopText>
        <ul className={styles.formulaHelpList}>
            <li>
                Reference columns with single quotes: <code>'Column Name'</code>
            </li>
            <li>
                Operators: <code>+</code> <code>-</code> <code>*</code> <code>/</code>
            </li>
            <li>
                Functions: <code>SUM()</code> <code>AVG()</code> <code>COUNT()</code>{' '}
                <code>MAX()</code> <code>MIN()</code>
            </li>
            <li>
                Example: <code>'Total Revenue' / 'Total Units'</code>
            </li>
        </ul>
    </div>
)

const TYPE_OPTIONS = [
    { label: 'Balance', value: 'balance' },
    { label: 'Transactional', value: 'transactional' },
    { label: 'Derived', value: 'derived' },
    { label: 'Derived Balance', value: 'derived_balance' },
    { label: 'Analysis Guidance', value: 'analysis_guidance' },
]

const EMPTY_DEFINITION = {
    name: '',
    description: '',
    formula: '',
    format: '',
    type: 'balance',
    dataset: '',
    dataset_id: '',
    column_name: '',
    thresholds: '',
}

/**
 * DefinitionTable - Reusable table for editing business term definitions
 *
 * @param {Array} definitions - Array of definition objects
 * @param {Function} onChange - Callback when definitions change
 * @param {boolean} disabled - Whether the table is disabled
 * @param {string} emptyMessage - Message to show when no definitions exist
 * @param {boolean} showAdvancedColumns - Whether to show dataset/thresholds columns (default: false)
 */
export const DefinitionTable = ({
    definitions = [],
    onChange,
    disabled = false,
    emptyMessage = 'No definitions yet. Add a term below to get started.',
    showAdvancedColumns = false,
}) => {
    const [newTerm, setNewTerm] = useState({ ...EMPTY_DEFINITION })

    const handleDefinitionChange = (index, field, value) => {
        const updated = [...definitions]
        updated[index] = { ...updated[index], [field]: value }
        onChange(updated)
    }

    const handleNewTermChange = (field, value) => {
        setNewTerm((prev) => ({ ...prev, [field]: value }))
    }

    const handleAdd = () => {
        // Parse thresholds if it's a JSON string
        const termToAdd = { ...newTerm }
        if (termToAdd.thresholds && typeof termToAdd.thresholds === 'string') {
            try {
                termToAdd.thresholds = JSON.parse(termToAdd.thresholds)
            } catch {
                // Keep as string if not valid JSON
            }
        }
        onChange([...definitions, termToAdd])
        setNewTerm({ ...EMPTY_DEFINITION })
    }

    const handleRemove = (index) => {
        onChange(definitions.filter((_, i) => i !== index))
    }

    const formatThresholds = (thresholds) => {
        if (!thresholds) return ''
        if (typeof thresholds === 'string') return thresholds
        return JSON.stringify(thresholds)
    }

    const isNewTermValid = newTerm.name.trim() !== ''

    // Base columns (always shown)
    const baseColumns = [
        {
            field: 'name',
            header: 'Term Name',
            width: 160,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'name', e.target.value)}
                    placeholder="Term Name"
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'description',
            header: 'Description',
            flex: 1.5,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTooltip
                    title={value || ''}
                    placement="top"
                    className={styles.descriptionTooltip}
                >
                    <div className={styles.descriptionWrapper}>
                        <ScoopTextField
                            value={value || ''}
                            onChange={(e) =>
                                handleDefinitionChange(rowIndex, 'description', e.target.value)
                            }
                            placeholder="Description"
                            disabled={disabled}
                        />
                    </div>
                </ScoopTooltip>
            ),
        },
        {
            field: 'formula',
            header: 'Formula',
            flex: 1.5,
            sortable: false,
            headerRender: () => (
                <span className={styles.formulaHeader}>
                    Formula
                    <ScoopTooltip title={<FormulaSyntaxHelp />} placement="bottom">
                        <InfoIcon size={14} className={styles.headerInfoIcon} />
                    </ScoopTooltip>
                </span>
            ),
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'formula', e.target.value)}
                    placeholder="Formula"
                    className={styles.codeInput}
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'format',
            header: 'Format',
            width: 80,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'format', e.target.value)}
                    placeholder="0.0%"
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'type',
            header: 'Type',
            width: 140,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopSelector
                    value={value || 'balance'}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'type', e.target.value)}
                    options={TYPE_OPTIONS}
                    size="sm"
                    disabled={disabled}
                />
            ),
        },
    ]

    // Advanced columns (only shown when showAdvancedColumns is true)
    const advancedColumns = [
        {
            field: 'dataset',
            header: 'Dataset',
            width: 140,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'dataset', e.target.value)}
                    placeholder="Dataset name"
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'dataset_id',
            header: 'Dataset ID',
            width: 90,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handleDefinitionChange(rowIndex, 'dataset_id', e.target.value)}
                    placeholder="I12345"
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'column_name',
            header: 'Column',
            width: 120,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) =>
                        handleDefinitionChange(rowIndex, 'column_name', e.target.value)
                    }
                    placeholder="Column"
                    disabled={disabled}
                />
            ),
        },
        {
            field: 'thresholds',
            header: 'Thresholds',
            width: 120,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTooltip
                    title={formatThresholds(value) || 'No thresholds set'}
                    placement="top"
                >
                    <div className={styles.descriptionWrapper}>
                        <ScoopTextField
                            value={formatThresholds(value)}
                            onChange={(e) => {
                                const val = e.target.value
                                try {
                                    const parsed = JSON.parse(val)
                                    handleDefinitionChange(rowIndex, 'thresholds', parsed)
                                } catch {
                                    handleDefinitionChange(rowIndex, 'thresholds', val)
                                }
                            }}
                            placeholder="{}"
                            disabled={disabled}
                            className={styles.codeInput}
                        />
                    </div>
                </ScoopTooltip>
            ),
        },
    ]

    // Actions column (always shown)
    const actionsColumn = {
        field: 'actions',
        header: ' ',
        width: 48,
        sortable: false,
        resizable: false,
        render: ({ rowIndex }) => (
            <ScoopButton
                variant="grey"
                size="xs"
                onClick={() => handleRemove(rowIndex)}
                disabled={disabled}
                className={styles.deleteButton}
            >
                <TrashIcon size={16} />
            </ScoopButton>
        ),
    }

    // Combine columns based on showAdvancedColumns prop
    const columns = showAdvancedColumns
        ? [...baseColumns, ...advancedColumns, actionsColumn]
        : [...baseColumns, actionsColumn]

    return (
        <div className={styles.container}>
            <div className={styles.tableWrapper}>
                <ScoopTable
                    columns={columns}
                    rows={definitions}
                    emptyMessage={emptyMessage}
                    sorting={{ enabled: false }}
                    pagination={{ enabled: false }}
                    resizable={false}
                    domLayout="normal"
                    rowHeight={52}
                    height="100%"
                />
            </div>

            {/* Input row for adding new terms */}
            <div className={styles.inputRow}>
                <ScoopRow
                    className={
                        showAdvancedColumns ? styles.inputRowFieldsAdvanced : styles.inputRowFields
                    }
                >
                    <ScoopTextField
                        value={newTerm.name}
                        onChange={(e) => handleNewTermChange('name', e.target.value)}
                        placeholder="Term Name"
                        disabled={disabled}
                    />
                    <ScoopTextField
                        value={newTerm.description}
                        onChange={(e) => handleNewTermChange('description', e.target.value)}
                        placeholder="Description"
                        disabled={disabled}
                    />
                    <ScoopTextField
                        value={newTerm.formula}
                        onChange={(e) => handleNewTermChange('formula', e.target.value)}
                        placeholder="Formula"
                        disabled={disabled}
                        className={styles.codeInput}
                    />
                    <ScoopTextField
                        value={newTerm.format}
                        onChange={(e) => handleNewTermChange('format', e.target.value)}
                        placeholder="Format"
                        disabled={disabled}
                    />
                    <ScoopSelector
                        value={newTerm.type}
                        onChange={(e) => handleNewTermChange('type', e.target.value)}
                        options={TYPE_OPTIONS}
                        size="sm"
                        disabled={disabled}
                    />
                    {showAdvancedColumns && (
                        <>
                            <ScoopTextField
                                value={newTerm.dataset}
                                onChange={(e) => handleNewTermChange('dataset', e.target.value)}
                                placeholder="Dataset"
                                disabled={disabled}
                            />
                            <ScoopTextField
                                value={newTerm.dataset_id}
                                onChange={(e) => handleNewTermChange('dataset_id', e.target.value)}
                                placeholder="Dataset ID"
                                disabled={disabled}
                            />
                            <ScoopTextField
                                value={newTerm.column_name}
                                onChange={(e) => handleNewTermChange('column_name', e.target.value)}
                                placeholder="Column"
                                disabled={disabled}
                            />
                            <ScoopTextField
                                value={newTerm.thresholds}
                                onChange={(e) => handleNewTermChange('thresholds', e.target.value)}
                                placeholder="Thresholds"
                                disabled={disabled}
                                className={styles.codeInput}
                            />
                        </>
                    )}
                </ScoopRow>
                <ScoopButton
                    variant="grey"
                    size="sm"
                    onClick={handleAdd}
                    disabled={disabled || !isNewTermValid}
                    leftSection={<PlusIcon size={16} />}
                    className={styles.addButton}
                >
                    Add Term
                </ScoopButton>
            </div>
        </div>
    )
}
